import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Eye, EyeOff } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Form, Formik } from 'formik'
import {
  resetPasswordInitialValues,
  resetPasswordValidationSchema,
} from '@/pages/ResetPassword/validation'
import { useState } from 'react'
import { confirmResetPassword } from '@/redux/asyncThunks/user'
import { useAppDispatch } from '@/redux/hooks'
import { toast } from 'sonner'
import ROUTES from '@/routes/routes'
import { UserRole } from '@/constants/room'

const ResetPassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const params = new URLSearchParams(location.search)
  const uid = params.get('uid')
  const token = params.get('token')

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const handleSubmit = async (values: typeof resetPasswordInitialValues) => {
    if (uid && token) {
      const formData = {
        uid,
        token,
        new_password1: values.password,
        new_password2: values.confirmPassword,
      }

      try {
        const resultAction = await dispatch(confirmResetPassword(formData))
        if (confirmResetPassword.rejected.match(resultAction)) {
          toast.error('Invalid uid or Token')
        } else {
          toast.success(resultAction.payload.detail)

          // For first-time setup, navigate to dashboard based on user role
          if (resultAction.payload.is_first_time_setup) {
            const user = resultAction.payload.user
            if (user?.role === UserRole.Student) {
              navigate(ROUTES.STUDENT.DASHBOARD, { replace: true })
            } else if (user?.role === UserRole.Professor) {
              navigate(ROUTES.PROFESSOR.ROOMS, { replace: true })
            } else {
              navigate(ROUTES.LOGIN, { replace: true })
            }
          } else {
            // For forgot password reset, navigate to login
            navigate(ROUTES.LOGIN, { replace: true })
          }
        }
      } catch (error) {
        console.error(error)
      }
    } else {
      toast.error('uid or token missing in params')
    }
  }

  return (
    <div className='flex h-screen w-screen flex-col md:flex-row'>
      <div className='relative hidden flex-1 md:block'>
        <img
          src='/images/auth-background.svg'
          alt='Auth background'
          className='absolute w-1/2 object-cover md:h-full md:w-5/6'
        />
        <div className='flex h-full items-center justify-center'>
          <img
            src='/images/auth-avatar.svg'
            alt='Auth Avatar'
            className='absolute z-10 object-fill md:h-full md:w-auto'
          />
        </div>
      </div>
      <div className='flex flex-1 flex-col items-center justify-center'>
        <div className='flex justify-center'>
          <img
            src='/images/oge.svg'
            alt='Huble logo'
            className='w-1/2 object-contain md:w-full'
          />
        </div>
        <div className='flex w-3/4 flex-col items-center md:w-full'>
          <Card className='w-full max-w-md border-none shadow-none'>
            <CardHeader className='space-y-3 flex flex-col items-center'>
              <CardTitle className='text-4xl font-bold leading-[50px]'>
                Welcome to OGE
              </CardTitle>
              <CardTitle className='text-2xl text-[#023572] font-bold'>
                Set Password
              </CardTitle>
              <CardDescription className='text-base text-black font-normal'>
                Create a new, strong password that you don’t use for other
                websites.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Formik
                initialValues={resetPasswordInitialValues}
                validationSchema={resetPasswordValidationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  isSubmitting,
                }) => (
                  <Form className='space-y-4'>
                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.password && errors.password
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='password'
                      >
                        Create Password
                      </Label>
                      <div className='relative'>
                        <Input
                          className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                            touched.password && errors.password
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                          id='password'
                          name='password'
                          type={showPassword ? 'text' : 'password'}
                          placeholder='At least 8 characters'
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <Eye className='h-4 w-4 text-gray-400' />
                          ) : (
                            <EyeOff className='h-4 w-4 text-gray-400' />
                          )}
                        </Button>
                      </div>
                      {touched.password && errors.password && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.password}
                        </div>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.confirmPassword && errors.confirmPassword
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='confirmPassword'
                      >
                        Confirm Password
                      </Label>
                      <div className='relative'>
                        <Input
                          className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                            touched.confirmPassword && errors.confirmPassword
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                          id='confirmPassword'
                          name='confirmPassword'
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder='At least 8 characters'
                          value={values.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          {showConfirmPassword ? (
                            <Eye className='h-4 w-4 text-gray-400' />
                          ) : (
                            <EyeOff className='h-4 w-4 text-gray-400' />
                          )}
                        </Button>
                      </div>
                      {touched.confirmPassword && errors.confirmPassword && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.confirmPassword}
                        </div>
                      )}
                    </div>

                    <div className='flex justify-between items-center'>
                      <Button
                        type='submit'
                        disabled={isSubmitting}
                        className='w-40 h-[38px] text-base font-normal bg-dark-blue hover:bg-dark-blue-hovered'
                      >
                        Save Password
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
